import { Feature, uniqueFeatureProperties } from "@cartographerio/geometry";
import { checkExhausted, raise } from "@cartographerio/util";
import lodash from "lodash";
import { Attribute } from "../attribute";
import { marker, MarkerStyle } from "../marker";
import { lookupBucket, rangeBucket } from "./create";
import { valueLabel } from "./functions";
import { Bucket, PropertyType, PropertyValue } from "./type";

function simpleAttributeBuckets(marker: MarkerStyle) {
  return [rangeBucket(null, null, marker, "All values", false)];
}

function yearToEpoch(year: number): number {
  return new Date(year, 0, 1, 0, 0, 0, 0).getTime();
}

function epochToYear(epoch: number): number {
  return new Date(epoch).getFullYear();
}

function yearAndMonthToEpoch(year: number, monthIndex: number): number {
  return new Date(year, monthIndex, 1, 0, 0, 0, 0).getTime();
}

function monthLabel(year: number, monthIndex: number): string {
  switch (monthIndex) {
    case 0:
      return `Jan ${year}`;
    case 1:
      return `Feb ${year}`;
    case 2:
      return `Mar ${year}`;
    case 3:
      return `Apr ${year}`;
    case 4:
      return `May ${year}`;
    case 5:
      return `Jun ${year}`;
    case 6:
      return `Jul ${year}`;
    case 7:
      return `Aug ${year}`;
    case 8:
      return `Sep ${year}`;
    case 9:
      return `Oct ${year}`;
    case 10:
      return `Nov ${year}`;
    case 11:
      return `Dec ${year}`;
    default:
      return raise(new Error(`Bad month index: ${monthIndex}`));
  }
}

function autoBuckets(
  propertyType: PropertyType,
  propertyName: string,
  visibleFeatures: Feature[],
  ignoreNulls?: boolean,
  numBuckets?: number
): Bucket[] {
  const uniqueValues = uniqueFeatureProperties(
    visibleFeatures,
    propertyName
  ) as PropertyValue[];

  if (uniqueValues.length === 0) {
    return simpleAttributeBuckets(marker.standard);
  } else {
    switch (propertyType) {
      case "string":
      case "boolean": {
        const hasNulls = uniqueValues.includes(null);

        const nonNullBuckets = uniqueValues
          .filter(value => value != null)
          .map((value, index) =>
            lookupBucket(
              value,
              value == null ? marker.empty : marker.lookup(index),
              valueLabel(value, propertyType)
            )
          );

        if (hasNulls && !ignoreNulls) {
          return [...nonNullBuckets, lookupBucket(null, marker.empty)];
        } else {
          return nonNullBuckets;
        }
      }

      case "number": {
        let hasNulls = false;
        let minValue = Number.POSITIVE_INFINITY;
        let maxValue = Number.NEGATIVE_INFINITY;

        uniqueValues.forEach(value => {
          if (typeof value === "number") {
            minValue = Math.min(minValue, value);
            maxValue = Math.max(maxValue, value);
          } else {
            hasNulls = true;
          }
        });

        if (
          Number.isFinite(minValue) &&
          Number.isFinite(maxValue) &&
          maxValue > minValue
        ) {
          const numSteps = numBuckets ?? Math.min(uniqueValues.length, 10);
          const stepInc = (maxValue - minValue) / numSteps;

          const numericBuckets = lodash.range(0, numSteps).map(index =>
            rangeBucket(
              minValue + (index + 0) * stepInc,
              minValue + (index + 1) * stepInc,
              marker.numeric(index / numSteps),
              undefined,
              index === numSteps - 1 // last bucket is inclusive,
            )
          );

          if (hasNulls && !ignoreNulls) {
            return [...numericBuckets, lookupBucket(null, marker.empty)];
          } else {
            return numericBuckets;
          }
        } else {
          return simpleAttributeBuckets(marker.standard);
        }
      }

      case "timestamp": {
        let hasNulls = false;
        let minValue = Number.POSITIVE_INFINITY;
        let maxValue = Number.NEGATIVE_INFINITY;

        uniqueValues.forEach(value => {
          if (typeof value === "number") {
            minValue = Math.max(minValue, value);
            maxValue = Math.min(maxValue, value);
          } else {
            hasNulls = true;
          }
        });

        if (
          Number.isFinite(minValue) &&
          Number.isFinite(maxValue) &&
          maxValue > minValue
        ) {
          const rawMinYear = epochToYear(minValue);
          const rawMaxYear = epochToYear(maxValue);

          const minYear = rawMinYear < 2000 ? 2000 : rawMinYear;

          const maxYear = rawMaxYear < rawMinYear ? rawMinYear : rawMaxYear;

          const underflowBucket: Bucket[] =
            rawMinYear === minYear
              ? []
              : [
                  rangeBucket(
                    null,
                    new Date(minYear, 0, 1, 0, 0, 0, 0).getTime(),
                    marker.hue(10),
                    `Before ${minYear}`,
                    false
                  ),
                ];

          const overflowBucket: Bucket[] =
            rawMaxYear === maxYear
              ? []
              : [
                  rangeBucket(
                    new Date(maxYear + 1, 0, 1, 0, 0, 0, 0).getTime(),
                    null,
                    marker.hue(350),
                    `After ${maxYear}`,
                    false
                  ),
                ];

          const emptyBucket =
            hasNulls && !ignoreNulls ? [lookupBucket(null, marker.empty)] : [];

          let numericBuckets: Bucket[];

          if (maxYear > minYear) {
            numericBuckets = lodash.range(minYear, maxYear).map(year => {
              return rangeBucket(
                yearToEpoch(year),
                yearToEpoch(year),
                marker.hue(60 + (240 * (year - minYear)) / (maxYear - minYear)),
                `${year}`,
                year === maxYear // last bucket is inclusive,
              );
            });
          } else {
            const year = minYear;
            numericBuckets = lodash.range(0, 12).map(month => {
              return rangeBucket(
                yearAndMonthToEpoch(year, month),
                yearAndMonthToEpoch(year, month + 1),
                marker.hue(60 + (240 * month) / 12),
                monthLabel(year, month),
                month === 12 // last bucket is inclusive,
              );
            });
          }

          return [
            ...underflowBucket,
            ...numericBuckets,
            ...overflowBucket,
            ...emptyBucket,
          ];
        } else {
          return simpleAttributeBuckets(marker.standard);
        }
      }

      default:
        return checkExhausted(propertyType);
    }
  }
}

export function attributeBuckets(
  attr: Attribute,
  visibleFeatures: Feature[] = []
): Bucket[] {
  switch (attr.type) {
    case "SimpleAttribute":
      return simpleAttributeBuckets(attr.marker);

    case "PropertyAttribute":
      switch (attr.buckets.type) {
        case "StandardBuckets":
          return attr.buckets.buckets;

        case "AutoBuckets":
          return autoBuckets(
            attr.propertyType,
            attr.propertyName,
            visibleFeatures,
            attr.buckets.ignoreNulls,
            attr.buckets.numBuckets
          );
      }
  }
}
